import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AI_FEEDBACK, AI_FEEDBACK_LABELS, ENTITY_TYPE_LABELS, RELATION_KIND_LABELS, type AiFeedback, type RelationKind } from '@raro/domain';
import { Button, EmptyState, Label, Notice, Tag, cx } from '@raro/ui';
import { useState } from 'react';
import { Link } from 'react-router';
import { FEATURE_LABELS, SUGGESTION_KIND_LABELS, useAiStatus } from '../lib/ai';
import { api } from '../lib/api';
import type { Suggestion } from '../lib/types';

const citeLabel = (type: string) => (type === 'chunk' ? 'trecho' : (ENTITY_TYPE_LABELS[type as keyof typeof ENTITY_TYPE_LABELS] ?? type));

export function useSuggestionActions(projectId: string) {
  const qc = useQueryClient();
  const done = () => {
    void qc.invalidateQueries({ queryKey: ['suggestions', projectId] });
    void qc.invalidateQueries({ queryKey: ['summary', projectId] });
    void qc.invalidateQueries({ queryKey: ['signals', projectId] });
    void qc.invalidateQueries({ queryKey: ['observations', projectId] });
    void qc.invalidateQueries({ queryKey: ['questions', projectId] });
    void qc.invalidateQueries({ queryKey: ['contradictions', projectId] });
    void qc.invalidateQueries({ queryKey: ['evidence'] });
    void qc.invalidateQueries({ queryKey: ['source'] });
    void qc.invalidateQueries({ queryKey: ['hypothesis'] });
    void qc.invalidateQueries({ queryKey: ['ai-costs', projectId] });
    void qc.invalidateQueries({ queryKey: ['overview'] });
  };
  const accept = useMutation({ mutationFn: (id: string) => api.post(`/suggestions/${id}/accept`), onSuccess: done });
  const reject = useMutation({ mutationFn: ({ id, feedback }: { id: string; feedback: AiFeedback }) => api.post(`/suggestions/${id}/reject`, { feedback }), onSuccess: done });
  return { accept, reject };
}

export function SuggestionCard({ s, projectId, compact }: { s: Suggestion; projectId: string; compact?: boolean }) {
  const { accept, reject } = useSuggestionActions(projectId);
  const [rejecting, setRejecting] = useState(false);
  const pending = s.status === 'pending';
  const text = s.kind === 'evidence' ? s.payload.content : s.kind === 'relation' ? `${s.payload.fromText} → ${RELATION_KIND_LABELS[s.payload.relationKind as RelationKind] ?? s.payload.relationKind} → ${s.payload.toText}` : s.kind === 'hypothesis' ? (s.payload.statement as string | undefined) ?? s.payload.text : s.payload.text;
  const isQuestion = s.kind === 'cycle_question';
  return (
    <article className={cx('rr-sugg', !pending && 'rr-sugg--decided')}>
      <div>
        <div className="rr-sugg__kind">
          <Tag tone="accent">{SUGGESTION_KIND_LABELS[s.kind] ?? s.kind}</Tag>
          <span>{FEATURE_LABELS[s.feature] ?? s.feature}</span>
          {s.payload.sourceTitle && !compact && <span>· {s.payload.sourceTitle}</span>}
          {s.payload.sessionTitle != null && !compact && <span>· sessão: {String(s.payload.sessionTitle)}</span>}
          {!pending && <span>· {s.status === 'accepted' ? 'aceita' : `rejeitada${s.feedback ? ` (${AI_FEEDBACK_LABELS[s.feedback]})` : ''}`}</span>}
        </div>
        {s.kind === 'hypothesis' && <p className="rr-sugg__title">{String(s.payload.title ?? '')}</p>}
        {isQuestion && <p className="rr-sugg__title" style={{ color: 'var(--rr-accent)' }}>pergunta candidata para o ciclo</p>}
        <p className={cx('rr-sugg__text', (s.kind === 'evidence' || isQuestion) && 'rr-sugg__text--quote')}>{s.kind === 'evidence' ? `“${text}”` : text}</p>
        {isQuestion && s.payload.whatChanges != null && <p className="rr-sugg__why"><span className="rr-label">o que muda se for esta</span> {String(s.payload.whatChanges)}</p>}
        {s.kind === 'hypothesis' && s.payload.wouldBeWrongIf != null && <p className="rr-sugg__why"><span className="rr-label" style={{ color: 'var(--rr-down)' }}>estaria errada se</span> {String(s.payload.wouldBeWrongIf)}</p>}
        {s.kind === 'hypothesis' && Array.isArray(s.payload.contradictions) && (s.payload.contradictions as unknown[]).length > 0 && <p className="rr-sugg__why"><span className="rr-label" style={{ color: 'var(--rr-down)' }}>confronto</span> {(s.payload.contradictions as unknown[]).length} item(ns) do material contradizem ou enfraquecem; viram relações «contradiz» se aceita.</p>}
        {s.kind === 'hypothesis' && Array.isArray(s.payload.questions) && (s.payload.questions as string[]).length > 0 && <ul className="rr-sugg__list">{(s.payload.questions as string[]).map((q, i) => <li key={i}>antes de apostar: {q}</li>)}</ul>}
        {s.rationale && <p className="rr-sugg__why">{s.rationale}</p>}
        {s.payload.citations && s.payload.citations.length > 0 && (
          <div className="rr-sugg__cites">
            <span>vem de:</span>
            {s.payload.citations.map((c, i) => (
              <span key={`${c.id}-${i}`}>{c.sourceId ? <Link to={`/fontes/${c.sourceId}`}>{citeLabel(c.type)}</Link> : citeLabel(c.type)}</span>
            ))}
          </div>
        )}
      </div>
      <div className="rr-sugg__actions">
        {s.confidence !== null && <span className="rr-conf">confiança {Math.round(s.confidence * 100)}%</span>}
        {pending && !rejecting && (
          <>
            <Button size="sm" variant="primary" onClick={() => accept.mutate(s.id)} disabled={accept.isPending}>
              {s.kind === 'evidence' ? 'aceitar como evidência' : s.kind === 'relation' ? 'criar relação' : s.kind === 'hypothesis' ? 'seguir esta hipótese (sócio)' : isQuestion ? 'bater o martelo: esta é a pergunta (sócio)' : `aceitar ${SUGGESTION_KIND_LABELS[s.kind]}`}
            </Button>
            <Button size="sm" variant="quiet" onClick={() => setRejecting(true)}>rejeitar</Button>
          </>
        )}
        {pending && rejecting && (
          <div className="rr-feedback">
            {AI_FEEDBACK.filter((f) => f !== 'useful').map((f) => (
              <button key={f} type="button" onClick={() => reject.mutate({ id: s.id, feedback: f })}>{AI_FEEDBACK_LABELS[f]}</button>
            ))}
            <button type="button" onClick={() => setRejecting(false)}>voltar</button>
          </div>
        )}
        {(accept.isError || reject.isError) && <span className="rr-field__error">{((accept.error ?? reject.error) as Error).message}</span>}
      </div>
    </article>
  );
}

/** Seção MACHINE do projeto: TORNAR VISÍVEL + fila de sugestões para julgamento. */
export function MachineSection({ projectId }: { projectId: string }) {
  const status = useAiStatus();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<'pending' | 'all'>('pending');
  const list = useQuery({ queryKey: ['suggestions', projectId, filter], queryFn: () => api.get<Suggestion[]>(`/projects/${projectId}/suggestions?status=${filter}`) });
  const makeVisible = useMutation({
    mutationFn: () => api.post<{ suggestions: Suggestion[]; model: string; latencyMs: number }>('/ai/make-visible', { projectId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['suggestions', projectId] });
      void qc.invalidateQueries({ queryKey: ['ai-costs', projectId] });
    },
  });
  const frame = useMutation({
    mutationFn: () => api.post<{ suggestions: Suggestion[]; model: string; latencyMs: number }>('/ai/frame', { projectId }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['suggestions', projectId] }); void qc.invalidateQueries({ queryKey: ['ai-costs', projectId] }); },
  });
  const find = useMutation({
    mutationFn: () => api.post<{ suggestions: Suggestion[]; model: string; latencyMs: number }>('/ai/find', { projectId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['suggestions', projectId] });
      void qc.invalidateQueries({ queryKey: ['ai-costs', projectId] });
    },
  });
  const llm = Boolean(status.data?.llm);
  return (
    <section className="rr-section">
      <div className="rr-section__head">
        <Label>Machine</Label>
        <div className="rr-row">
          <button type="button" className="rr-label" style={{ color: filter === 'pending' ? 'var(--rr-text)' : undefined }} onClick={() => setFilter('pending')}>pendentes</button>
          <button type="button" className="rr-label" style={{ color: filter === 'all' ? 'var(--rr-text)' : undefined }} onClick={() => setFilter('all')}>todas</button>
        </div>
      </div>
      <div className="rr-card rr-row rr-row--between rr-row--wrap" style={{ gap: 'var(--rr-space-4)' }}>
        <div className="rr-stack rr-stack--1">
          <span className="rr-heading">tornar visível</span>
          <span className="rr-faint" style={{ fontSize: 'var(--rr-text-sm)' }}>a Machine procura padrões, tensões, recorrências e o que está nos dados mas ainda não foi nomeado. você julga.</span>
          {status.data && !llm && <span className="rr-field__error">sem modelo configurado. defina ANTHROPIC_API_KEY (ou AI_LLM_PROVIDER=mock).</span>}
          {status.data?.llm && <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>modelo forte: {status.data.llm.models.strong} · {status.data.llm.provider}</span>}
        </div>
        <Button variant="primary" onClick={() => makeVisible.mutate()} disabled={!llm || makeVisible.isPending}>
          {makeVisible.isPending ? 'a Machine está olhando…' : 'tornar visível →'}
        </Button>
      </div>
      {makeVisible.isError && <Notice tone="error">{(makeVisible.error as Error).message}</Notice>}
      {makeVisible.isSuccess && <Notice>{makeVisible.data.suggestions.length} achado(s) em {(makeVisible.data.latencyMs / 1000).toFixed(1)}s com {makeVisible.data.model}. nada vira fato sem você.</Notice>}
      <div className="rr-card rr-row rr-row--between rr-row--wrap" style={{ gap: 'var(--rr-space-4)', borderColor: 'var(--rr-accent)' }}>
        <div className="rr-stack rr-stack--1">
          <span className="rr-heading">enquadrar</span>
          <span className="rr-faint" style={{ fontSize: 'var(--rr-text-sm)' }}>a pergunta do ciclo é o produto do VER. a partir de tudo que foi coletado (sessões, entrevistas, pesquisa, planilhas, SAC, vendas), a Machine propõe até 3 perguntas candidatas, com o que as sustenta. um sócio bate o martelo; ela guia os próximos 90 dias.</span>
        </div>
        <Button variant="primary" onClick={() => frame.mutate()} disabled={!llm || frame.isPending}>
          {frame.isPending ? 'a Machine está lendo tudo…' : 'enquadrar →'}
        </Button>
      </div>
      {frame.isError && <Notice tone="error">{(frame.error as Error).message}</Notice>}
      {frame.isSuccess && <Notice>{frame.data.suggestions.length} pergunta(s) candidata(s) em {(frame.data.latencyMs / 1000).toFixed(1)}s com {frame.data.model}. só um sócio bate o martelo.</Notice>}
      <div className="rr-card rr-row rr-row--between rr-row--wrap" style={{ gap: 'var(--rr-space-4)' }}>
        <div className="rr-stack rr-stack--1">
          <span className="rr-heading">encontrar</span>
          <span className="rr-faint" style={{ fontSize: 'var(--rr-text-sm)' }}>a partir da pergunta do ciclo e de tudo que foi coletado, a Machine propõe hipóteses com o que as sustenta e o que as derrubaria. um sócio bate o martelo: seguir, investigar ou descartar.</span>
        </div>
        <Button variant="primary" onClick={() => find.mutate()} disabled={!llm || find.isPending}>
          {find.isPending ? 'a Machine está procurando…' : 'encontrar →'}
        </Button>
      </div>
      {find.isError && <Notice tone="error">{(find.error as Error).message}</Notice>}
      {find.isSuccess && <Notice>{find.data.suggestions.length} hipótese(s) propostas em {(find.data.latencyMs / 1000).toFixed(1)}s com {find.data.model}. só um sócio aceita.</Notice>}
      {list.isSuccess && list.data.length === 0 && <EmptyState title={filter === 'pending' ? 'nada aguardando julgamento.' : 'nenhuma sugestão ainda.'}>sugestões chegam da leitura das fontes, de TORNAR VISÍVEL, de REMAR CONTRA e de “o que conecta isso?”.</EmptyState>}
      <div className="rr-stack rr-stack--3">{list.data?.map((s) => <SuggestionCard key={s.id} s={s} projectId={projectId} />)}</div>
    </section>
  );
}
